// A Penyedia paket project's home (POC; /project/:id for a project of kind
// "paket"). One scroller on a narrow frame; from a 960px main the paket
// column on the left and the project column on the right, each scrolling on
// its own (the same container split as the folder project page).
//
//   header      paket name (h1), instansi, HPS in Rupiah, the deadline
//               countdown, the LPSE link, the pencil (Ubah info paket);
//               the moreH Menu (Ganti nama, Hapus)
//   intake      while the paket info is empty, the chat greets with "Paket
//               apa yang mau diikuti?" and three chips (Tempel pengumuman
//               LPSE, Unggah dokumen pemilihan, Ceritakan singkat); Pluto
//               answers with a proposal card (project/PaketProposalCard.tsx)
//   stages      the four stage tabs of Simulasi Pluto v4 (any can be
//               opened; the paket's own stage is marked), the note that
//               drafting in order is advised, not required, then the viewed
//               stage's numbered sections (project/PaketStages.tsx) with
//               its checklist folded under them: the steps generated from
//               the supplier process database by metode, jenis and
//               kualifikasi, grouped by process group, Pluto's items last,
//               each with a checkbox, Rincian, Catatan, Buat dokumen and
//               Buat dengan arahan (the draft opens in the right panel,
//               ./ActivityDocs.tsx, never as a new page); Download gabungan dokumen tahap ini
//               after the stage's sections and Download gabungan semua dokumen in the
//               header put the saved documents in one Word file (./MergeDocs.tsx)
//   tools       Reviu Persyaratan, Quality Gate and Kalkulator RAB open as
//               a full work view in place of the stage, with a way back
//   actions     on the paket's own stage: Lanjut ke tahap berikutnya; at the end of Pelaksanaan
//               Pemilihan, Terpilih and Tidak terpilih (closing with an
//               optional sanggah note and lessons); a quiet step back to the
//               previous stage
//   chat        the project chat (ChatScreen with projectId): Pluto acts as
//               the stage-aware consultant
//   column      Pendampingan (Minta pendampingan konsultan, the requests with
//               their status and the consultant's notes), Chat di project
//               ini, File project; from 1024px its left edge drags it wider or
//               narrower (./PanelResizer.tsx)
import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import * as API from "../api";
import type { ChecklistGroup, ChecklistItem, ChecklistStage, Consultation, PaketStage, Project, ProjectDetail, ProjectFile } from "../api";
import type { ProjectsApi } from "../shell/Projects";
import { ChatScreen } from "../screens/Chat";
import { Dialog, Field, Notice, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { Menu } from "../ui/Menu";
import { DECISION_STAGE, FIRST_STAGE, NEXT_STAGE, PREVIOUS_STAGE, STAGE_HINT, STAGE_LABEL, paketTitle } from "./stages";
import { ActivityDocButtons, ActivityDocPanelSlot, ActivityDocStatus, ActivityDocsProvider, SequenceNote, canActivityDocs } from "./ActivityDocs";
import { MergeDocsButton, canMergeDocs } from "./MergeDocs";
import { ConsultationNotes, ConsultationStatusLine, PaketHeader, StepDetail } from "./PaketParts";
import { STAGE_DISCLAIMER, StageHeading, StageTabs, StageView, TOOL_LABEL, type WorkTool } from "./PaketStages";
import { KalkulatorRab, QualityGate, ReviuPersyaratan } from "./work";
import { useFeatures } from "../shell/features";
import { canDocx, useDocx } from "../pbj/docx";
import { canDraft } from "../pbj/drafts";
import { paketIsEmpty, usePaket } from "./PaketContext";
import { PaketEditDialog } from "./PaketEditDialog";
import { PanelResizer } from "./PanelResizer";
import { PANEL_ID, PanelHead, PanelToggle, useSidePanel } from "./SidePanel";
import { INTAKE_PREFILL, chipsForStage } from "./stage-chips";
import { SuggestionChips } from "../chat/SuggestionChips";
import { useUser } from "../auth";
import { ProjectChats } from "./ProjectChats";
import { ProjectFilesPanel } from "./ProjectFiles";

export interface PaketProjectProps {
  project: Project;
  projects: ProjectsApi;
}

type Prefill = { text: string; seq: number } | null;

export function PaketProject({ project: listed, projects }: PaketProjectProps) {
  const paket = usePaket();
  const [panelOpen] = useSidePanel();
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const features = useFeatures();
  // A save from the proposal card bumps the version: the header and checklist reload.
  const detail = useAsync<ProjectDetail>(() => API.getProject(listed.id), [listed.id, paket?.version ?? 0]);
  const [prefill, setPrefill] = useState<Prefill>(null);
  const [editing, setEditing] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const project = detail.data?.project ?? listed;
  const empty = paketIsEmpty(project);
  const user = useUser();
  // Stage chips hide once their checklist item is done.
  const doneKeys = (detail.data?.checklist ?? []).flatMap((g) => g.items.filter((i) => i.done).map((i) => i.key));
  const stageChips = chipsForStage(project.stage, doneKeys);

  const fill = (text: string) => {
    setPrefill((p) => ({ text, seq: (p?.seq ?? 0) + 1 }));
  };

  const saveEdit = async (input: API.PaketSaveInput) => {
    setEditBusy(true);
    setEditError(null);
    try {
      if (paket) await paket.save(input);
      else detail.set(await API.updatePaket(project.id, input));
      setEditing(false);
    } catch (err) {
      setEditError(API.errorMessage(err));
    } finally {
      setEditBusy(false);
    }
  };

  // A move or a rename elsewhere keeps the shared list (sidebar) in step.
  const applyDetail = (next: ProjectDetail) => {
    detail.set(next);
    const rows = projects.list.data ?? [];
    projects.list.set(rows.map((r) => (r.id === next.project.id ? { ...r, ...next.project } : r)));
  };

  let flow: ReactNode;
  if (detail.loading && !detail.data) flow = <SkeletonRows count={4} label="Memuat paket" />;
  else if (detail.error && !detail.data) {
    flow = (
      <Notice
        tone="danger"
        title="Paket belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={detail.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(detail.error)}</p>
      </Notice>
    );
  } else if (detail.data) {
    flow = (
      <PaketFlow
        detail={detail.data}
        onEdit={() => {
          setEditError(null);
          setEditing(true);
        }}
        onChange={applyDetail}
        onItem={(item) => detail.set(withItem(detail.data!, item))}
      />
    );
  }

  return (
    <ActivityDocsProvider projectId={project.id}>
    <div className="pj pk" data-view="paket" data-panel={panelOpen ? "open" : "closed"}>
      <div className="pk-layout">
        <div className="pk-main" data-ui-scroller="" data-intake={empty ? "" : undefined}>
          <div className="pk-section">
            <PaketHeader
              project={project}
              progress={progressOf(detail.data?.checklist)}
              onEdit={() => {
                setEditError(null);
                setEditing(true);
              }}
              action={
                <span className="pj-head-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm pk-head-btn"
                  aria-haspopup="dialog"
                  onClick={() => {
                    setEditError(null);
                    setEditing(true);
                  }}
                >
                  <Glyph name="edit2" size={16} />
                  <span>Edit Project</span>
                </button>
                <button type="button" className="btn btn-secondary btn-sm pk-head-btn" aria-haspopup="dialog" onClick={() => projects.openDelete(project)}>
                  <Glyph name="trash" size={16} />
                  <span>Hapus</span>
                </button>
                {canMergeDocs(features) ? <MergeDocsButton projectId={project.id} label="Download gabungan semua dokumen" size="sm" modeHint="title" /> : null}
                <PanelToggle />
                </span>
              }
            />
          </div>
          <div className="pk-section">{flow}</div>
          <div className="pj-chat">
            <ChatScreen
              projectId={project.id}
              projectName={project.name}
              greeting={empty ? "Paket apa yang mau diikuti?" : "Tanya Pluto, konsultan paket Anda"}
              greetingLevel="h2"
              prefill={prefill}
              dock={dock}
              suggestions={empty ? undefined : stageChips}
              greetingMessage={{
                text: paketGreeting(firstName(user.name), project, detail.data?.checklist ?? []),
                chips: empty ? <IntakeChips onFill={fill} /> : <SuggestionChips chips={stageChips} onPick={(c) => fill(c.prompt)} />,
              }}
            />
          </div>
        </div>
        {panelOpen ? <PanelResizer /> : null}
        <aside id={PANEL_ID} className="pj-aside pk-aside" aria-label="Isi project" data-ui-scroller="">
          <PanelHead />
          <ActivityDocPanelSlot>
            <Pendampingan project={project} />
            <ProjectChats projectId={project.id} />
            <ProjectFilesPanel projectId={project.id} />
          </ActivityDocPanelSlot>
        </aside>
        <div ref={setDock} className="pj-dock no-print" />
      </div>
      <PaketEditDialog open={editing} project={project} busy={editBusy} error={editError} onSubmit={(input) => void saveEdit(input)} onClose={() => !editBusy && setEditing(false)} />
    </div>
    </ActivityDocsProvider>
  );
}

/** The paket intake on an empty paket: what to give Pluto, and three quick actions. */
/** The paket intake's quick replies under Pluto's greeting on an empty paket. */
export function IntakeChips({ onFill }: { onFill: (text: string) => void }) {
  const upload = () => {
    // The composer's own file picker (PDF or DOCX of the dokumen pemilihan).
    document.querySelector<HTMLInputElement>(".pk .composer input[type='file'], .composer input[type='file']")?.click();
  };
  return (
    <div className="pk-intake">
      <div className="chat-chips guest-chips pk-intake-chips" role="group" aria-label="Cara mengisi info paket">
        <button type="button" className="chip chat-chip" onClick={() => onFill(INTAKE_PREFILL.paste)}>
          <Glyph name="documentText" size={16} />
          <span>Tempel pengumuman LPSE</span>
        </button>
        <button type="button" className="chip chat-chip" onClick={upload}>
          <Glyph name="paperclip" size={16} />
          <span>Unggah dokumen pemilihan</span>
        </button>
        <button type="button" className="chip chat-chip" onClick={() => onFill(INTAKE_PREFILL.tell)}>
          <Glyph name="messages" size={16} />
          <span>Ceritakan singkat</span>
        </button>
      </div>
    </div>
  );
}

/** The first word of a name, for a friendly greeting. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** Pluto's greeting on a paket home (UI only): the intake on an empty paket, else the stage and its checklist. */
export function paketGreeting(first: string, project: Project, checklist: ChecklistGroup[]): string {
  if (paketIsEmpty(project)) {
    return `Halo ${first}, saya Pluto, konsultan Anda untuk project ini. Paket apa yang mau diikuti? Tempel teks pengumuman dari LPSE, unggah dokumen pemilihan, atau ceritakan singkat.`;
  }
  const stage = project.stage ?? FIRST_STAGE;
  const name = paketTitle(project);
  if (stage === "selesai") return `Halo ${first}, paket ${name} sudah selesai. Mau mencatat pelajaran untuk paket berikutnya?`;
  if (stage === "ditutup") return `Halo ${first}, paket ${name} sudah ditutup. Mau mencatat pelajaran untuk paket berikutnya?`;
  const items = checklist.find((g) => g.stage === stage)?.items ?? [];
  const done = items.filter((i) => i.done).length;
  const progress = items.length > 0 ? ` ${done} dari ${items.length} butir checklist tahap ini sudah selesai.` : "";
  return `Halo ${first}, paket ${name} sedang di tahap ${STAGE_LABEL[stage]}.${progress} Mau lanjut dari mana?`;
}

/** A stage's items by process group in list order; Pluto's items (no step detail) last. */
export function sections(items: ChecklistItem[]): { title: string; items: ChecklistItem[] }[] {
  const out: { title: string; items: ChecklistItem[] }[] = [];
  const pluto: ChecklistItem[] = [];
  for (const item of items) {
    if (!item.detail) {
      pluto.push(item);
      continue;
    }
    const last = out.at(-1);
    if (last && last.title === item.detail.kelompok) last.items.push(item);
    else out.push({ title: item.detail.kelompok, items: [item] });
  }
  if (pluto.length > 0) out.push({ title: "Dari Pluto", items: pluto });
  return out;
}

function withItem(d: ProjectDetail, item: ChecklistItem): ProjectDetail {
  return { ...d, checklist: d.checklist.map((g) => ({ ...g, items: g.items.map((i) => (i.id === item.id ? item : i)) })) };
}

function moveError(err: unknown): string {
  return API.errorMessage(err);
}

// ---------------------------------------------------------------------------
// Stepper, checklist, actions
// ---------------------------------------------------------------------------
interface PaketFlowProps {
  detail: ProjectDetail;
  /** Lengkapi Data in the Persiapan Pemilihan stage: the paket info dialog. */
  onEdit: () => void;
  onChange: (next: ProjectDetail) => void;
  onItem: (item: ChecklistItem) => void;
}

/** Done and total steps over every stage (the header's Progres badge); none while loading. */
export function progressOf(checklist: ChecklistGroup[] | undefined): { done: number; total: number } | undefined {
  if (!checklist) return undefined;
  const items = checklist.flatMap((g) => g.items);
  return { done: items.filter((i) => i.done).length, total: items.length };
}

/** The stage tab an end status opens on: ditutup on Pelaksanaan Pemilihan, selesai on Serah Terima. */
export function viewedStageOf(stage: PaketStage): ChecklistStage {
  if (stage === "ditutup") return "pelaksanaan_pemilihan";
  if (stage === "selesai") return "serah_terima";
  return stage;
}

function PaketFlow({ detail, onEdit, onChange, onItem }: PaketFlowProps) {
  const { project } = detail;
  const stage: PaketStage = project.stage ?? FIRST_STAGE;
  const group: ChecklistGroup | undefined = detail.checklist.find((g) => g.stage === stage);
  const [viewed, setViewed] = useState<ChecklistStage>(viewedStageOf(stage));
  const [tool, setTool] = useState<WorkTool | null>(null);
  // A stage move opens the paket's new stage.
  useEffect(() => {
    setViewed(viewedStageOf(stage));
    setTool(null);
  }, [stage]);
  const features = useFeatures();
  const libraryOn = features.list !== null && (canDocx(features) || canDraft(features));
  const library = useAsync(() => (libraryOn ? API.getTemplates() : Promise.resolve(null)), [libraryOn]);
  const templates = library.data?.side === "penyedia" ? library.data.templates : [];
  const viewedGroup = detail.checklist.find((g) => g.stage === viewed);
  // The paket's own stage tab (ditutup sits on Pelaksanaan Pemilihan, selesai on Serah Terima).
  const own = viewed === viewedStageOf(stage);
  const view = (next: ChecklistStage) => {
    setTool(null);
    setViewed(next);
  };
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmNext, setConfirmNext] = useState(false);
  const [closing, setClosing] = useState(false);

  const move = async (body: API.StageMove, key: string) => {
    setBusy(key);
    setError(null);
    try {
      onChange(await API.moveStage(project.id, body));
      setConfirmNext(false);
      setClosing(false);
    } catch (err) {
      setError(moveError(err));
    } finally {
      setBusy(null);
    }
  };

  const next = NEXT_STAGE[stage];
  const prev = PREVIOUS_STAGE[stage];
  const open = group ? group.items.filter((i) => !i.done).length : 0;

  const lanjut = () => {
    if (!next) return;
    if (open > 0) setConfirmNext(true);
    else void move({ to: next }, "next");
  };

  const checklist = viewedGroup ? (
    <div className="pk-checklist-groups" aria-label={`Checklist tahap ${STAGE_LABEL[viewed]}`} role="group">
      {sections(viewedGroup.items).map((sec) => (
        <section key={sec.title} className="pk-checklist-group" aria-label={sec.title}>
          <h4 className="pk-checklist-group-title">{sec.title}</h4>
          <ul className="pk-checklist">
            {sec.items.map((item) => (
              <ChecklistRow key={item.id} projectId={project.id} item={item} onSaved={onItem} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  ) : (
    <p className="pks-caption">Belum ada aktivitas untuk tahap ini.</p>
  );

  if (tool) {
    const Tool = tool === "reviu" ? ReviuPersyaratan : tool === "quality" ? QualityGate : KalkulatorRab;
    return (
      <section className="pk-flow" aria-label={TOOL_LABEL[tool]} data-tool-view={tool}>
        <button type="button" className="pk-back-btn" onClick={() => setTool(null)}>
          <Glyph name="arrowLeft" size={16} />
          <span>Kembali ke tahap {STAGE_LABEL[viewed]}</span>
        </button>
        <Tool projectId={project.id} />
      </section>
    );
  }

  return (
    <section className="pk-flow" aria-labelledby="pk-stage-title">
      <StageTabs current={stage} viewed={viewed} checklist={detail.checklist} onView={view} />
      <SequenceNote />
      <StageHeading viewed={viewed} current={stage} items={viewedGroup?.items ?? []} />
      <StageView
        detail={detail}
        viewed={viewed}
        templates={templates}
        checklist={checklist}
        checklistCount={viewedGroup?.items.length ?? 0}
        onTool={setTool}
        onView={view}
        onEdit={onEdit}
        onIntake={onChange}
      />
      {canMergeDocs(features) ? (
        <div className="pbj-docs-merge pk-docs-merge">
          <MergeDocsButton key={viewed} projectId={detail.project.id} stage={viewed} label="Download gabungan dokumen tahap ini" />
        </div>
      ) : null}

      {stage === "ditutup" && project.closingNote ? (
        <div className="pk-closing">
          <h3 className="pk-closing-title">Catatan sanggah dan pelajaran</h3>
          <p className="pk-closing-body">{project.closingNote}</p>
        </div>
      ) : null}

      {error ? (
        <Notice tone="danger" title="Tahap belum berubah">
          <p>{error}</p>
        </Notice>
      ) : null}

      {own ? (
      <div className="pk-actions">
        {stage === DECISION_STAGE ? (
          <>
            <button type="button" className="btn btn-primary" disabled={busy !== null} aria-busy={busy === "won" || undefined} onClick={() => void move({ to: "pelaksanaan_kontrak", outcome: "terpilih" }, "won")}>
              <Glyph name="tickCircle" size={18} />
              <span>Terpilih</span>
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => setClosing(true)}>
              <span>Tidak terpilih</span>
            </button>
          </>
        ) : next ? (
          <button type="button" className="btn btn-primary" disabled={busy !== null} aria-busy={busy === "next" || undefined} onClick={lanjut}>
            <span>Lanjut ke tahap berikutnya</span>
            <Glyph name="chevronRight" size={18} />
          </button>
        ) : null}
        {prev ? (
          <button type="button" className="pk-back-btn" disabled={busy !== null} aria-busy={busy === "back" || undefined} onClick={() => void move({ to: prev }, "back")}>
            <Glyph name="arrowLeft" size={16} />
            <span>Kembali ke {STAGE_LABEL[prev]}</span>
          </button>
        ) : null}
      </div>
      ) : null}
      {own && stage === DECISION_STAGE ? <p className="pk-next-hint">Setelah pengumuman pemenang, tandai hasilnya. Terpilih berlanjut ke {STAGE_LABEL.pelaksanaan_kontrak}.</p> : null}
      {own && next && stage !== DECISION_STAGE ? <p className="pk-next-hint">Berikutnya: {STAGE_LABEL[next]}</p> : null}
      <p className="pks-caption">{STAGE_DISCLAIMER}</p>

      <Dialog
        open={confirmNext}
        title={`Lanjut ke tahap ${next ? STAGE_LABEL[next] : ""}?`}
        size="sm"
        busy={busy === "next"}
        onClose={() => setConfirmNext(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmNext(false)} disabled={busy === "next"}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" onClick={() => next && void move({ to: next }, "next")} disabled={busy === "next"} aria-busy={busy === "next" || undefined}>
              Lanjut
            </button>
          </>
        }
      >
        <p className="dialog-text">
          Masih ada {open} butir checklist tahap {STAGE_LABEL[stage]} yang belum selesai. Anda tetap dapat kembali ke tahap ini nanti.
        </p>
      </Dialog>
      <CloseDialog open={closing} busy={busy === "lost"} onClose={() => setClosing(false)} onConfirm={(note) => void move({ to: "ditutup", outcome: "tidak_terpilih", note: note || null }, "lost")} />
    </section>
  );
}

/** Tidak terpilih: the paket closes, with an optional sanggah note and lessons learned. */
function CloseDialog({ open, busy, onClose, onConfirm }: { open: boolean; busy: boolean; onClose: () => void; onConfirm: (note: string) => void }) {
  const id = useId();
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) setNote("");
  }, [open]);
  return (
    <Dialog
      open={open}
      title="Tutup paket ini?"
      description="Paket ditandai tidak terpilih. Chat dan file tetap tersimpan."
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm(note.trim())} disabled={busy} aria-busy={busy || undefined}>
            Tutup paket
          </button>
        </>
      }
    >
      <Field id={`${id}-note`} label="Catatan sanggah dan pelajaran (opsional)" hint="Mis. alasan tidak terpilih, sanggah yang diajukan, dan hal yang diperbaiki untuk paket berikutnya.">
        <textarea id={`${id}-note`} data-autofocus="" rows={4} maxLength={4000} value={note} aria-describedby={`${id}-note-hint`} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>
    </Dialog>
  );
}

interface ChecklistRowProps {
  projectId: string;
  item: ChecklistItem;
  onSaved: (item: ChecklistItem) => void;
}

function ChecklistRow({ projectId, item, onSaved }: ChecklistRowProps) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  // Word of a generated step's Dokumen / Output Bukti, filled from this paket (no Pluto turn).
  const features = useFeatures();
  const docx = useDocx();
  const stepWord = !!item.detail && /^\d{2}-\d{2}$/.test(item.key) && features.list !== null && canDocx(features);

  // The document this step produces (its Output Bukti, or the step itself for one without).
  const docTitle = item.detail?.output || item.label;

  const save = async (patch: API.ChecklistPatch) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await API.updateChecklistItem(projectId, item.id, patch));
      return true;
    } catch (err) {
      setError(API.errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="pk-item" data-done={item.done ? "" : undefined}>
      <div className="pk-item-row">
        <label className="pk-check" htmlFor={`${id}-done`}>
          <input id={`${id}-done`} type="checkbox" checked={item.done} disabled={busy} onChange={(e) => void save({ done: e.currentTarget.checked })} />
          <span className="pk-item-text">
            <span className="pk-item-label">{item.label}</span>
            {item.detail?.output ? <span className="pk-item-sub">Output: {item.detail.output}</span> : null}
          </span>
        </label>
        <div className="pk-item-actions">
          {item.detail ? (
            <button
              type="button"
              className="ui-icon-btn pk-detail-btn"
              aria-expanded={showDetail}
              aria-controls={`${id}-detail`}
              aria-label={`Rincian ${item.label}`}
              title="Rincian"
              onClick={() => setShowDetail((v) => !v)}
            >
              <Glyph name="infoCircle" />
            </button>
          ) : null}
          {stepWord ? (
            <button
              type="button"
              className="ui-icon-btn pk-word-btn"
              disabled={docx.busy !== null}
              aria-busy={docx.busy !== null || undefined}
              aria-label={`Unduh Word ${item.detail?.output || item.label}`}
              title="Unduh Word"
              onClick={() => void docx.start(item.key, () => API.downloadStepDocx(item.key, projectId))}
            >
              <Glyph name="documentDownload" />
            </button>
          ) : null}
          {canActivityDocs(features) ? <ActivityDocButtons activityKey={item.key} title={docTitle} /> : null}
          <button
            type="button"
            className="ui-icon-btn pk-note-btn"
            aria-expanded={editing}
            aria-controls={`${id}-editor`}
            aria-label={`Catatan dan file untuk ${item.label}`}
            title="Catatan dan file"
            onClick={() => setEditing((e) => !e)}
          >
            <Glyph name="edit2" />
          </button>
        </div>
      </div>
      <ActivityDocStatus activityKey={item.key} title={docTitle} />
      {docx.error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{docx.error.message}</span>
        </p>
      ) : null}
      {showDetail && item.detail ? <StepDetail id={`${id}-detail`} detail={item.detail} /> : null}
      {!editing && (item.note || item.attachmentId) ? (
        <div className="pk-item-meta">
          {item.note ? <p className="pk-item-note">{item.note}</p> : null}
          {item.attachmentId ? <AttachedName projectId={projectId} attachmentId={item.attachmentId} /> : null}
        </div>
      ) : null}
      {editing ? (
        <ItemEditor
          id={`${id}-editor`}
          projectId={projectId}
          item={item}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (note, attachmentId) => {
            if (await save({ note: note || null, attachmentId })) setEditing(false);
          }}
        />
      ) : null}
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </li>
  );
}

/** The project's files that have an attachment row (the ones a checklist item may point to). */
function useAttachableFiles(projectId: string, enabled: boolean) {
  return useAsync<ProjectFile[]>(() => (enabled ? API.listProjectFiles(projectId).then((r) => r.files.filter((f) => f.attachmentId)) : Promise.resolve([])), [projectId, enabled]);
}

function AttachedName({ projectId, attachmentId }: { projectId: string; attachmentId: string }) {
  const files = useAttachableFiles(projectId, true);
  const file = files.data?.find((f) => f.attachmentId === attachmentId);
  return (
    <p className="pk-item-file">
      <Glyph name="paperclip" size={16} />
      <span>{file ? file.name : files.loading ? "Memuat file" : "File project"}</span>
    </p>
  );
}

interface ItemEditorProps {
  id: string;
  projectId: string;
  item: ChecklistItem;
  busy: boolean;
  onCancel: () => void;
  onSave: (note: string, attachmentId: string | null) => void;
}

function ItemEditor({ id, projectId, item, busy, onCancel, onSave }: ItemEditorProps) {
  const [note, setNote] = useState(item.note ?? "");
  const [file, setFile] = useState(item.attachmentId ?? "");
  const files = useAttachableFiles(projectId, true);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave(note.trim(), file || null);
  };
  return (
    <form id={id} className="pk-editor" onSubmit={submit}>
      <Field id={`${id}-note`} label="Catatan">
        <textarea id={`${id}-note`} rows={3} maxLength={2000} value={note} readOnly={busy} aria-describedby={`${id}-note-hint`} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>
      <Field id={`${id}-file`} label="File project" hint={files.data && files.data.length === 0 ? "Belum ada file. Unggah lewat File project." : undefined}>
        <select id={`${id}-file`} value={file} disabled={busy || files.loading} aria-describedby={`${id}-file-hint`} onChange={(e) => setFile(e.currentTarget.value)}>
          <option value="">Tanpa file</option>
          {(files.data ?? []).map((f) => (
            <option key={f.attachmentId!} value={f.attachmentId!}>
              {f.name}
            </option>
          ))}
        </select>
      </Field>
      <div className="pk-editor-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Batal
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          Simpan
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Pendampingan: a human consultant on request
// ---------------------------------------------------------------------------
function Pendampingan({ project }: { project: Project }) {
  const list = useAsync<Consultation[]>(() => API.listConsultations(project.id), [project.id]);
  const [open, setOpen] = useState(false);
  const rows = list.data ?? [];

  const added = (c: Consultation) => list.set([c, ...rows.filter((r) => r.id !== c.id)]);
  const updated = (c: Consultation) => list.set(rows.map((r) => (r.id === c.id ? c : r)));

  let body: ReactNode;
  if (list.loading && !list.data) body = <SkeletonRows count={1} label="Memuat pendampingan" />;
  else if (list.error) {
    body = (
      <Notice
        tone="danger"
        title="Pendampingan belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={list.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(list.error)}</p>
      </Notice>
    );
  } else if (rows.length === 0) {
    body = <p className="pj-empty">Pluto mendampingi Anda di setiap tahap lewat chat project. Bila perlu, minta konsultan untuk meninjau langsung paket ini.</p>;
  } else {
    body = (
      <ul className="pd-list" aria-label="Permintaan pendampingan">
        {rows.map((c) => (
          <OwnerRequest key={c.id} projectId={project.id} consultation={c} onUpdated={updated} />
        ))}
      </ul>
    );
  }

  return (
    <section className="pj-panel pk-consult" aria-labelledby="pk-consult-title">
      <div className="pj-panel-head">
        <h2 id="pk-consult-title" className="pj-panel-title">
          Pendampingan
        </h2>
      </div>
      <button type="button" className="btn btn-secondary pk-consult-btn" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <Glyph name="profile2user" size={18} />
        <span>Minta pendampingan konsultan</span>
      </button>
      {body}
      <ConsultationDialog
        open={open}
        project={project}
        onClose={() => setOpen(false)}
        onCreated={(c) => {
          added(c);
          setOpen(false);
        }}
      />
    </section>
  );
}

function OwnerRequest({ projectId, consultation: c, onUpdated }: { projectId: string; consultation: Consultation; onUpdated: (c: Consultation) => void }) {
  const id = useId();
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onUpdated(await API.addOwnerNote(projectId, c.id, reply.trim()));
      setReply("");
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="pd-item">
      <div className="pd-item-head">
        <h3 className="pd-topic">{c.topic}</h3>
        <span className="pd-stage">Tahap {STAGE_LABEL[c.stage]}</span>
      </div>
      <ConsultationStatusLine consultation={c} />
      <p className="pd-message">{c.message}</p>
      <ConsultationNotes consultation={c} viewer="pemilik" />
      {c.consultantId && c.status !== "selesai" ? (
        <form className="pd-reply" onSubmit={send}>
          <label className="visually-hidden" htmlFor={`${id}-reply`}>
            Balas konsultan
          </label>
          <textarea id={`${id}-reply`} rows={2} maxLength={4000} placeholder="Balas konsultan" value={reply} readOnly={busy} onChange={(e) => setReply(e.currentTarget.value)} />
          <button type="submit" className="btn btn-secondary" disabled={busy || !reply.trim()} aria-busy={busy || undefined}>
            Kirim
          </button>
        </form>
      ) : null}
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </li>
  );
}

function ConsultationDialog({ open, project, onClose, onCreated }: { open: boolean; project: Project; onClose: () => void; onCreated: (c: Consultation) => void }) {
  const id = useId();
  const formId = `${id}-form`;
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setTopic("");
    setMessage("");
    setError(null);
  }, [open]);
  const ready = topic.trim().length > 0 && message.trim().length > 0 && !busy;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(await API.requestConsultation(project.id, topic.trim(), message.trim()));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      title="Minta pendampingan konsultan"
      description={`Konsultan akan meninjau paket ${paketTitle(project)} (tahap ${STAGE_LABEL[project.stage ?? FIRST_STAGE]}) dan menjawab di sini.`}
      busy={busy}
      onClose={onClose}
      footer={
        <button type="submit" form={formId} className="btn btn-primary" disabled={!ready} aria-busy={busy || undefined}>
          Kirim permintaan
        </button>
      }
    >
      <form id={formId} className="pj-form" onSubmit={submit} noValidate>
        <Field id={`${id}-topic`} label="Topik" required>
          <input id={`${id}-topic`} data-autofocus="" autoComplete="off" maxLength={200} placeholder="mis. Tinjau metodologi pelaksanaan" value={topic} readOnly={busy} aria-describedby={`${id}-topic-hint`} onChange={(e) => setTopic(e.currentTarget.value)} />
        </Field>
        <Field id={`${id}-message`} label="Pesan" required error={error}>
          <textarea
            id={`${id}-message`}
            rows={5}
            maxLength={4000}
            placeholder="Ceritakan yang Anda butuhkan: dokumen yang perlu ditinjau, pertanyaan, atau kendala."
            value={message}
            readOnly={busy}
            aria-describedby={`${id}-message-hint`}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setMessage(e.currentTarget.value)}
          />
        </Field>
      </form>
    </Dialog>
  );
}
